import React, { useState, useEffect } from 'react';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Calendar, 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  User,
  Check,
  X,
  Clock,
  Palmtree,
  AlertCircle,
  HeartPulse,
  FileText,
  Sun,
  Moon,
  Users,
  BarChart2,
  Settings,
  CalendarDays
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIPO_CONFIG = {
  ferias: { label: 'Férias', color: 'bg-blue-500', bgLight: 'bg-blue-50', textColor: 'text-blue-600', icon: Palmtree },
  falta: { label: 'Falta', color: 'bg-red-500', bgLight: 'bg-red-50', textColor: 'text-red-600', icon: AlertCircle },
  baixa: { label: 'Baixa Médica', color: 'bg-orange-500', bgLight: 'bg-orange-50', textColor: 'text-orange-600', icon: HeartPulse },
  licenca: { label: 'Licença', color: 'bg-purple-500', bgLight: 'bg-purple-50', textColor: 'text-purple-600', icon: FileText },
};

const ESTADO_CONFIG = {
  pendente: { label: 'Pendente', color: 'bg-amber-500' },
  aprovado: { label: 'Aprovado', color: 'bg-green-500' },
  rejeitado: { label: 'Rejeitado', color: 'bg-red-500' },
};

export default function GestaoFerias() {
  const [ausencias, setAusencias] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDayDetail, setShowDayDetail] = useState(false);
  const [showBalancoDialog, setShowBalancoDialog] = useState(false);
  const [showHorariosDialog, setShowHorariosDialog] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [formData, setFormData] = useState({
    operador_id: '',
    data_inicio: '',
    data_fim: '',
    tipo: 'ferias',
    observacoes: ''
  });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendario');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [balancoOperadores, setBalancoOperadores] = useState([]);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ausenciasRes, operadoresRes, configRes] = await Promise.all([
        axios.get(`${API}/ausencias`),
        axios.get(`${API}/operadores`),
        axios.get(`${API}/configuracao`).catch(() => ({ data: null }))
      ]);
      
      setAusencias(ausenciasRes.data);
      setOperadores(operadoresRes.data.filter(o => o.perfil === 'operador'));
      setConfig(configRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Função para calcular dias úteis (excluindo feriados e fins de semana)
  const calcularDiasUteis = (dataInicio, dataFim) => {
    if (!dataInicio || !dataFim) return 0;
    
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    let diasUteis = 0;
    
    const diasTrabalho = config?.dias_trabalho || [1, 2, 3, 4, 5]; // Seg-Sex por defeito
    const feriados = config?.feriados?.map(f => f.data) || [];
    
    const current = new Date(inicio);
    while (current <= fim) {
      const diaSemana = current.getDay();
      const dataStr = current.toISOString().split('T')[0];
      
      // Verificar se é dia de trabalho e não é feriado
      if (diasTrabalho.includes(diaSemana) && !feriados.includes(dataStr)) {
        diasUteis++;
      }
      
      current.setDate(current.getDate() + 1);
    }
    
    return diasUteis;
  };

  // Calcular balanço de férias por operador
  const calcularBalancoOperadores = () => {
    const diasFeriasAnuais = config?.dias_ferias_anuais || 22; // 22 dias por defeito em Portugal
    
    const balanco = operadores.map(op => {
      // Filtrar ausências do operador no ano atual
      const ausenciasOp = ausencias.filter(a => 
        a.operador_id === op.id && 
        a.estado === 'aprovado' &&
        new Date(a.data_inicio).getFullYear() === currentYear
      );
      
      // Calcular dias por tipo
      const diasPorTipo = { ferias: 0, falta: 0, baixa: 0, licenca: 0 };
      
      ausenciasOp.forEach(a => {
        const dias = calcularDiasUteis(a.data_inicio, a.data_fim);
        if (diasPorTipo[a.tipo] !== undefined) {
          diasPorTipo[a.tipo] += dias;
        }
      });
      
      const feriasPendentes = ausencias.filter(a => 
        a.operador_id === op.id && 
        a.tipo === 'ferias' &&
        a.estado === 'pendente' &&
        new Date(a.data_inicio).getFullYear() === currentYear
      ).reduce((acc, a) => acc + calcularDiasUteis(a.data_inicio, a.data_fim), 0);
      
      return {
        operador: op,
        diasFeriasTotal: diasFeriasAnuais,
        diasFeriasUsados: diasPorTipo.ferias,
        diasFeriasPendentes: feriasPendentes,
        diasFeriasDisponiveis: diasFeriasAnuais - diasPorTipo.ferias - feriasPendentes,
        diasFaltas: diasPorTipo.falta,
        diasBaixa: diasPorTipo.baixa,
        diasLicenca: diasPorTipo.licenca,
        totalAusencias: ausenciasOp.length
      };
    });
    
    setBalancoOperadores(balanco);
    setShowBalancoDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.operador_id || !formData.data_inicio || !formData.data_fim) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    // Calcular dias úteis para informar o utilizador
    const diasUteis = calcularDiasUteis(formData.data_inicio, formData.data_fim);
    
    try {
      await axios.post(`${API}/ausencias`, {
        ...formData,
        dias_uteis: diasUteis
      });
      toast.success(`Ausência registada com sucesso (${diasUteis} dias úteis)`);
      setShowForm(false);
      setFormData({
        operador_id: '',
        data_inicio: '',
        data_fim: '',
        tipo: 'ferias',
        observacoes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Erro ao registar ausência');
    }
  };

  const handleAprovar = async (ausenciaId) => {
    try {
      await axios.patch(`${API}/ausencias/${ausenciaId}/aprovar`);
      toast.success('Ausência aprovada');
      loadData();
    } catch (error) {
      toast.error('Erro ao aprovar ausência');
    }
  };

  const handleRejeitar = async (ausenciaId) => {
    try {
      await axios.patch(`${API}/ausencias/${ausenciaId}/rejeitar`);
      toast.success('Ausência rejeitada');
      loadData();
    } catch (error) {
      toast.error('Erro ao rejeitar ausência');
    }
  };

  const handleDelete = async (ausenciaId) => {
    if (!window.confirm('Eliminar esta ausência?')) return;
    try {
      await axios.delete(`${API}/ausencias/${ausenciaId}`);
      toast.success('Ausência eliminada');
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar ausência');
    }
  };

  // Função para obter primeiro e último nome
  const getOperadorNomeAbreviado = (operadorId) => {
    const op = operadores.find(o => o.id === operadorId);
    if (!op?.nome_completo) return 'N/A';
    
    const partes = op.nome_completo.trim().split(' ');
    if (partes.length === 1) return partes[0];
    
    const primeiro = partes[0];
    const ultimo = partes[partes.length - 1];
    return `${primeiro} ${ultimo}`;
  };

  const getOperadorNomeCompleto = (operadorId) => {
    const op = operadores.find(o => o.id === operadorId);
    return op?.nome_completo || 'Desconhecido';
  };

  // Funções do calendário
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay };
  };

  const getAusenciasForDay = (day, estado = 'aprovado') => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return ausencias.filter(a => {
      const inicio = a.data_inicio;
      const fim = a.data_fim;
      const matchData = dateStr >= inicio && dateStr <= fim;
      const matchEstado = estado === 'todos' || a.estado === estado;
      return matchData && matchEstado;
    });
  };

  // Verificar se é dia de trabalho
  const isDiaTrabalho = (day) => {
    if (!config?.dias_trabalho) return true;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const diaSemana = date.getDay();
    return config.dias_trabalho.includes(diaSemana);
  };

  // Verificar se é feriado
  const getFeriado = (day) => {
    if (!config?.feriados) return null;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return config.feriados.find(f => f.data === dateStr);
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const handleDayClick = (day) => {
    const ausenciasDia = getAusenciasForDay(day, 'todos');
    const feriado = getFeriado(day);
    const diaTrabalho = isDiaTrabalho(day);
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    setSelectedDay({
      day,
      date: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day),
      dateStr,
      ausencias: ausenciasDia,
      feriado,
      diaTrabalho
    });
    setShowDayDetail(true);
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);

  // Filtrar ausências
  const ausenciasFiltradas = ausencias.filter(a => {
    const matchTipo = !filtroTipo || a.tipo === filtroTipo;
    const matchEstado = !filtroEstado || a.estado === filtroEstado;
    return matchTipo && matchEstado;
  });

  // Contadores
  const pendentes = ausencias.filter(a => a.estado === 'pendente').length;

  // Contar total de operadores ausentes hoje
  const hoje = new Date().toISOString().split('T')[0];
  const ausentesHoje = ausencias.filter(a => 
    a.estado === 'aprovado' && 
    a.data_inicio <= hoje && 
    a.data_fim >= hoje
  ).length;

  return (
    <GestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900 mb-2">RH - Férias e Ausências</h1>
            <p className="text-stone-600">Gerir ausências, férias e contabilização de dias por operador</p>
          </div>
          <div className="flex items-center gap-3">
            {ausentesHoje > 0 && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {ausentesHoje} ausente{ausentesHoje > 1 ? 's' : ''} hoje
              </span>
            )}
            {pendentes > 0 && (
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                {pendentes} pendente{pendentes > 1 ? 's' : ''}
              </span>
            )}
            {/* Botão Balanço de Férias */}
            <Button
              variant="outline"
              onClick={calcularBalancoOperadores}
              className="rounded-full"
              data-testid="btn-balanco-ferias"
            >
              <BarChart2 className="h-4 w-4 mr-2" />
              Balanço
            </Button>
            {/* Botão Horários */}
            <Button
              variant="outline"
              onClick={() => setShowHorariosDialog(true)}
              className="rounded-full"
              data-testid="btn-horarios"
            >
              <Clock className="h-4 w-4 mr-2" />
              Horários
            </Button>
            <div className="flex bg-stone-100 p-1 rounded-full">
              <Button
                variant={viewMode === 'calendario' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendario')}
                className="rounded-full"
              >
                <Calendar className="h-4 w-4 mr-1" />
                Calendário
              </Button>
              <Button
                variant={viewMode === 'lista' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('lista')}
                className="rounded-full"
              >
                Lista
              </Button>
            </div>
            <Button onClick={() => setShowForm(true)} className="rounded-full">
              <Plus className="h-4 w-4 mr-2" />
              Nova Ausência
            </Button>
          </div>
        </div>

        {/* Vista Calendário */}
        {viewMode === 'calendario' && (
          <Card className="p-6">
            {/* Navegação do mês */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={prevMonth}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Hoje
                </Button>
                <Button variant="outline" size="icon" onClick={nextMonth}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
              <h2 className="text-xl font-heading font-bold text-stone-900 capitalize">
                {currentMonth.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="flex items-center gap-2 text-sm text-stone-500">
                <Users className="h-4 w-4" />
                {operadores.length} operadores
              </div>
            </div>

            {/* Cabeçalho dias da semana */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((dia, idx) => {
                const isDiaConfig = config?.dias_trabalho?.includes(idx);
                return (
                  <div 
                    key={dia} 
                    className={`text-center text-sm font-medium py-2 rounded ${
                      isDiaConfig !== false ? 'text-stone-700' : 'text-stone-400'
                    }`}
                  >
                    {dia}
                  </div>
                );
              })}
            </div>

            {/* Grid do calendário */}
            <div className="grid grid-cols-7 gap-1">
              {/* Células vazias para os dias antes do mês começar */}
              {Array.from({ length: startingDay }).map((_, i) => (
                <div key={`empty-${i}`} className="h-28 bg-stone-50/50 rounded-lg" />
              ))}
              
              {/* Dias do mês */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const ausenciasDia = getAusenciasForDay(day);
                const isToday = new Date().toDateString() === new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toDateString();
                const feriado = getFeriado(day);
                const diaTrabalho = isDiaTrabalho(day);
                const isWeekend = !diaTrabalho;
                
                return (
                  <div 
                    key={day} 
                    onClick={() => handleDayClick(day)}
                    className={`h-28 p-2 rounded-lg border transition-all cursor-pointer hover:shadow-md ${
                      isToday ? 'border-primary border-2 bg-primary/5' : 
                      feriado ? 'border-amber-300 bg-amber-50' :
                      isWeekend ? 'border-stone-100 bg-stone-50' :
                      'border-stone-200 bg-white hover:border-stone-300'
                    }`}
                    data-testid={`day-${day}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${
                        isToday ? 'text-primary' : 
                        feriado ? 'text-amber-700' :
                        isWeekend ? 'text-stone-400' :
                        'text-stone-700'
                      }`}>
                        {day}
                      </span>
                      {feriado && (
                        <span className="text-[10px] text-amber-600 font-medium truncate max-w-[60px]" title={feriado.nome}>
                          {feriado.nome.split(' ')[0]}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 space-y-0.5 overflow-y-auto max-h-[70px]">
                      {ausenciasDia.slice(0, 3).map((a) => {
                        const tipoConfig = TIPO_CONFIG[a.tipo] || TIPO_CONFIG.ferias;
                        return (
                          <div 
                            key={a.id} 
                            className={`text-[11px] px-1.5 py-0.5 rounded ${tipoConfig.bgLight} ${tipoConfig.textColor} truncate font-medium`}
                            title={`${getOperadorNomeCompleto(a.operador_id)} - ${tipoConfig.label}`}
                          >
                            {getOperadorNomeAbreviado(a.operador_id)}
                          </div>
                        );
                      })}
                      {ausenciasDia.length > 3 && (
                        <span className="text-[10px] text-stone-500 font-medium">+{ausenciasDia.length - 3} mais</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legenda */}
            <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t">
              {Object.entries(TIPO_CONFIG).map(([tipo, tipoConfig]) => (
                <div key={tipo} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded ${tipoConfig.color}`}></div>
                  <span className="text-sm text-stone-600">{tipoConfig.label}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 ml-4 pl-4 border-l">
                <div className="w-3 h-3 rounded bg-amber-400"></div>
                <span className="text-sm text-stone-600">Feriado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-stone-300"></div>
                <span className="text-sm text-stone-600">Fim de Semana</span>
              </div>
            </div>
          </Card>
        )}

        {/* Vista Lista */}
        {viewMode === 'lista' && (
          <>
            {/* Filtros */}
            <Card className="p-4">
              <div className="flex gap-4">
                <select
                  value={filtroTipo}
                  onChange={(e) => setFiltroTipo(e.target.value)}
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                >
                  <option value="">Todos os tipos</option>
                  {Object.entries(TIPO_CONFIG).map(([tipo, tipoConfig]) => (
                    <option key={tipo} value={tipo}>{tipoConfig.label}</option>
                  ))}
                </select>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                  className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
                >
                  <option value="">Todos os estados</option>
                  {Object.entries(ESTADO_CONFIG).map(([estado, estadoConfig]) => (
                    <option key={estado} value={estado}>{estadoConfig.label}</option>
                  ))}
                </select>
              </div>
            </Card>

            {/* Lista de ausências */}
            {ausenciasFiltradas.length === 0 ? (
              <Card className="p-8 text-center">
                <Calendar className="h-12 w-12 mx-auto text-stone-300 mb-3" />
                <p className="text-stone-500">Nenhuma ausência registada</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {ausenciasFiltradas.map((ausencia) => {
                  const tipoConfig = TIPO_CONFIG[ausencia.tipo] || TIPO_CONFIG.ferias;
                  const estadoConfig = ESTADO_CONFIG[ausencia.estado] || ESTADO_CONFIG.pendente;
                  const TipoIcon = tipoConfig.icon;
                  
                  return (
                    <Card key={ausencia.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-full ${tipoConfig.bgLight} flex items-center justify-center`}>
                            <TipoIcon className={`h-6 w-6 ${tipoConfig.textColor}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-stone-900">
                                {getOperadorNomeAbreviado(ausencia.operador_id)}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs text-white ${estadoConfig.color}`}>
                                {estadoConfig.label}
                              </span>
                            </div>
                            <p className="text-sm text-stone-600">
                              <span className="font-medium">{tipoConfig.label}</span>
                              {' • '}
                              {new Date(ausencia.data_inicio).toLocaleDateString('pt-PT')} 
                              {' a '} 
                              {new Date(ausencia.data_fim).toLocaleDateString('pt-PT')}
                            </p>
                            {ausencia.observacoes && (
                              <p className="text-xs text-stone-500 mt-1">{ausencia.observacoes}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {ausencia.estado === 'pendente' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => handleAprovar(ausencia.id)}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Aprovar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleRejeitar(ausencia.id)}
                              >
                                <X className="h-4 w-4 mr-1" />
                                Rejeitar
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-stone-400 hover:text-red-500"
                            onClick={() => handleDelete(ausencia.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Dialog Nova Ausência */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Registar Ausência</DialogTitle>
            <DialogDescription>Preencha os dados da ausência do operador</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Operador *</label>
              <select
                value={formData.operador_id}
                onChange={(e) => setFormData({ ...formData, operador_id: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                required
              >
                <option value="">Selecione um operador</option>
                {operadores.map((op) => (
                  <option key={op.id} value={op.id}>
                    {getOperadorNomeAbreviado(op.id)} ({op.nome_completo})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Tipo *</label>
              <select
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg"
              >
                {Object.entries(TIPO_CONFIG).map(([tipo, tipoConfig]) => (
                  <option key={tipo} value={tipo}>{tipoConfig.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Data Início *</label>
                <Input
                  type="date"
                  value={formData.data_inicio}
                  onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Data Fim *</label>
                <Input
                  type="date"
                  value={formData.data_fim}
                  onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Observações</label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg min-h-[80px]"
                placeholder="Notas adicionais..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1">
                Registar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalhe do Dia */}
      <Dialog open={showDayDetail} onOpenChange={setShowDayDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {selectedDay?.date?.toLocaleDateString('pt-PT', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long',
                year: 'numeric'
              })}
            </DialogTitle>
            <DialogDescription>
              {selectedDay?.feriado ? (
                <span className="text-amber-600 font-medium">{selectedDay.feriado.nome}</span>
              ) : selectedDay?.diaTrabalho ? (
                <span className="text-green-600">Dia de trabalho</span>
              ) : (
                <span className="text-stone-500">Fim de semana / Folga</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Info do dia */}
            <div className="grid grid-cols-3 gap-3">
              <div className={`p-3 rounded-lg text-center ${selectedDay?.diaTrabalho ? 'bg-green-50' : 'bg-stone-100'}`}>
                <Sun className={`h-5 w-5 mx-auto mb-1 ${selectedDay?.diaTrabalho ? 'text-green-600' : 'text-stone-400'}`} />
                <span className="text-xs text-stone-600">
                  {selectedDay?.diaTrabalho ? 'Dia Útil' : 'Folga'}
                </span>
              </div>
              <div className={`p-3 rounded-lg text-center ${selectedDay?.feriado ? 'bg-amber-50' : 'bg-stone-100'}`}>
                <Palmtree className={`h-5 w-5 mx-auto mb-1 ${selectedDay?.feriado ? 'text-amber-600' : 'text-stone-400'}`} />
                <span className="text-xs text-stone-600">
                  {selectedDay?.feriado ? 'Feriado' : 'Normal'}
                </span>
              </div>
              <div className={`p-3 rounded-lg text-center ${selectedDay?.ausencias?.length > 0 ? 'bg-blue-50' : 'bg-stone-100'}`}>
                <Users className={`h-5 w-5 mx-auto mb-1 ${selectedDay?.ausencias?.length > 0 ? 'text-blue-600' : 'text-stone-400'}`} />
                <span className="text-xs text-stone-600">
                  {selectedDay?.ausencias?.length || 0} ausências
                </span>
              </div>
            </div>

            {/* Lista de ausências do dia */}
            {selectedDay?.ausencias?.length > 0 ? (
              <div className="space-y-2">
                <h3 className="font-semibold text-stone-900">Ausências neste dia:</h3>
                {selectedDay.ausencias.map((ausencia) => {
                  const tipoConfig = TIPO_CONFIG[ausencia.tipo] || TIPO_CONFIG.ferias;
                  const estadoConfig = ESTADO_CONFIG[ausencia.estado] || ESTADO_CONFIG.pendente;
                  const TipoIcon = tipoConfig.icon;
                  
                  return (
                    <div 
                      key={ausencia.id} 
                      className={`p-3 rounded-lg border ${tipoConfig.bgLight} border-transparent flex items-center justify-between`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full bg-white flex items-center justify-center`}>
                          <TipoIcon className={`h-5 w-5 ${tipoConfig.textColor}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-stone-900">
                            {getOperadorNomeAbreviado(ausencia.operador_id)}
                          </p>
                          <p className="text-xs text-stone-600">
                            {tipoConfig.label} • {new Date(ausencia.data_inicio).toLocaleDateString('pt-PT')} - {new Date(ausencia.data_fim).toLocaleDateString('pt-PT')}
                          </p>
                          {ausencia.observacoes && (
                            <p className="text-xs text-stone-500 mt-0.5">{ausencia.observacoes}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] text-white font-medium ${estadoConfig.color}`}>
                          {estadoConfig.label}
                        </span>
                        {ausencia.estado === 'pendente' && (
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-green-600 hover:bg-green-100"
                              onClick={() => { handleAprovar(ausencia.id); setShowDayDetail(false); }}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-red-600 hover:bg-red-100"
                              onClick={() => { handleRejeitar(ausencia.id); setShowDayDetail(false); }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-stone-500">
                <Calendar className="h-12 w-12 mx-auto mb-2 text-stone-300" />
                <p>Nenhuma ausência registada para este dia</p>
              </div>
            )}

            {/* Botão para adicionar ausência */}
            <Button 
              className="w-full" 
              onClick={() => {
                setFormData({
                  ...formData,
                  data_inicio: selectedDay?.dateStr || '',
                  data_fim: selectedDay?.dateStr || ''
                });
                setShowDayDetail(false);
                setShowForm(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Ausência para este Dia
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Balanço de Férias */}
      <Dialog open={showBalancoDialog} onOpenChange={setShowBalancoDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <BarChart2 className="h-5 w-5" />
              Balanço de Férias {currentYear}
            </DialogTitle>
            <DialogDescription>
              Contabilização de dias de férias e ausências por operador (apenas dias úteis)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Info sobre dias anuais */}
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-800">Dias de férias anuais por funcionário:</span>
                </div>
                <span className="text-2xl font-bold text-blue-600">{config?.dias_ferias_anuais || 22} dias</span>
              </div>
            </Card>

            {/* Tabela de balanço */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-100">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Operador</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Férias Usadas</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Pendentes</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Disponíveis</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Faltas</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Baixa</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Licença</th>
                  </tr>
                </thead>
                <tbody>
                  {balancoOperadores.map((item) => (
                    <tr key={item.operador.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <span className="font-medium text-stone-900">{item.operador.nome_completo}</span>
                            <p className="text-xs text-stone-500">@{item.operador.username}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-3 px-4">
                        <span className={`font-bold ${item.diasFeriasUsados > 0 ? 'text-blue-600' : 'text-stone-400'}`}>
                          {item.diasFeriasUsados}
                        </span>
                        <span className="text-xs text-stone-400 ml-1">/ {item.diasFeriasTotal}</span>
                      </td>
                      <td className="text-center py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                          item.diasFeriasPendentes > 0 ? 'bg-amber-100 text-amber-700' : 'text-stone-400'
                        }`}>
                          {item.diasFeriasPendentes}
                        </span>
                      </td>
                      <td className="text-center py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-sm font-bold ${
                          item.diasFeriasDisponiveis > 10 ? 'bg-green-100 text-green-700' :
                          item.diasFeriasDisponiveis > 0 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {item.diasFeriasDisponiveis}
                        </span>
                      </td>
                      <td className="text-center py-3 px-4">
                        <span className={`font-medium ${item.diasFaltas > 0 ? 'text-red-600' : 'text-stone-400'}`}>
                          {item.diasFaltas}
                        </span>
                      </td>
                      <td className="text-center py-3 px-4">
                        <span className={`font-medium ${item.diasBaixa > 0 ? 'text-orange-600' : 'text-stone-400'}`}>
                          {item.diasBaixa}
                        </span>
                      </td>
                      <td className="text-center py-3 px-4">
                        <span className={`font-medium ${item.diasLicenca > 0 ? 'text-purple-600' : 'text-stone-400'}`}>
                          {item.diasLicenca}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {balancoOperadores.length === 0 && (
              <div className="text-center py-8 text-stone-500">
                <Users className="h-12 w-12 mx-auto mb-2 text-stone-300" />
                <p>Nenhum operador encontrado</p>
              </div>
            )}

            {/* Resumo */}
            <Card className="p-4 bg-stone-50">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-xs text-stone-500">Total Operadores</p>
                  <p className="text-xl font-bold text-stone-900">{balancoOperadores.length}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500">Dias Férias Usados</p>
                  <p className="text-xl font-bold text-blue-600">
                    {balancoOperadores.reduce((acc, b) => acc + b.diasFeriasUsados, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-stone-500">Total Faltas</p>
                  <p className="text-xl font-bold text-red-600">
                    {balancoOperadores.reduce((acc, b) => acc + b.diasFaltas, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-stone-500">Total Baixas</p>
                  <p className="text-xl font-bold text-orange-600">
                    {balancoOperadores.reduce((acc, b) => acc + b.diasBaixa, 0)}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Horários */}
      <Dialog open={showHorariosDialog} onOpenChange={setShowHorariosDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Configuração de Horários
            </DialogTitle>
            <DialogDescription>
              Definir dias de trabalho, feriados e configurações de RH
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Dias de Trabalho */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-3">Dias de Trabalho</label>
              <div className="grid grid-cols-7 gap-2">
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((dia, idx) => {
                  const isActive = config?.dias_trabalho?.includes(idx) ?? [1,2,3,4,5].includes(idx);
                  return (
                    <button
                      key={dia}
                      type="button"
                      className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive 
                          ? 'bg-primary text-white' 
                          : 'bg-stone-100 text-stone-400'
                      }`}
                    >
                      {dia}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-stone-500 mt-2">
                * Para alterar os dias de trabalho, aceda às Configurações gerais.
              </p>
            </div>

            {/* Feriados */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-3">Feriados Configurados ({currentYear})</label>
              {config?.feriados?.length > 0 ? (
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {config.feriados
                    .filter(f => f.data.startsWith(currentYear.toString()))
                    .map((feriado, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg">
                      <span className="text-sm font-medium text-amber-800">{feriado.nome}</span>
                      <span className="text-sm text-amber-600">
                        {new Date(feriado.data).toLocaleDateString('pt-PT')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-stone-500 bg-stone-50 rounded-lg">
                  <p>Nenhum feriado configurado</p>
                  <p className="text-xs mt-1">Configure os feriados nas Configurações gerais.</p>
                </div>
              )}
            </div>

            {/* Dias de Férias Anuais */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Dias de Férias Anuais</label>
              <div className="flex items-center gap-4">
                <div className="flex-1 p-4 bg-blue-50 rounded-lg">
                  <span className="text-3xl font-bold text-blue-600">{config?.dias_ferias_anuais || 22}</span>
                  <span className="text-sm text-blue-600 ml-2">dias por funcionário</span>
                </div>
              </div>
              <p className="text-xs text-stone-500 mt-2">
                * Valor padrão em Portugal: 22 dias úteis. Ajuste nas Configurações se necessário.
              </p>
            </div>

            <div className="pt-4 border-t">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setShowHorariosDialog(false);
                  window.location.href = '/gestor/configuracoes';
                }}
              >
                <Settings className="h-4 w-4 mr-2" />
                Ir para Configurações Gerais
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </GestorLayout>
  );
}
